'use client';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuthContext } from './AuthProvider';
import { usePathname } from 'next/navigation';
import { ChevronDown, User, MessageCircle, ExternalLink, ArrowUpRight, Menu, X } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image'; 
import DepositModal from './DepositModal';
import UserNav from './UserNav';
import InboxDropdown from './InboxDropdown';

import { useLanguage } from '@/components/LanguageProvider';

const LOCAL_API_URL = process.env.NEXT_PUBLIC_LOCAL_API;

export default function Header() {
  const { isAuthenticated, user, isLoading, openLoginModal, logout } = useAuthContext();
  const pathname = usePathname();

  const { t } = useLanguage();
  const th = t.header;
  const notificationLabel = th.notifications;

  const [scrolled, setScrolled] = useState(false); 
  const [isModesOpen, setIsModesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileModesOpen, setIsMobileModesOpen] = useState(false);
  const [mobileModesHeight, setMobileModesHeight] = useState(0);
  const [mobileInboxHeight, setMobileInboxHeight] = useState(0);
  const [isDesktopViewport, setIsDesktopViewport] = useState(false);
  const [activeTab, setActiveTab] = useState<'multiverse' | 'creative' | 'all'>('multiverse');
  const modesRef = useRef<HTMLDivElement>(null);
  const modesMenuRef = useRef<HTMLDivElement>(null);
  const mobileModesContentRef = useRef<HTMLDivElement>(null);
  const mobileInboxContentRef = useRef<HTMLDivElement>(null);
  const desktopInboxTriggerRef = useRef<HTMLButtonElement>(null);

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadCount = useRef(0); 
  const isFirstCheck = useRef(true); 

  const checkUnreadNotifications = async () => {
    if (!user?.oauthId) return;
    try {
      const res = await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${user.oauthId}/unread-count`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();

        if (!isFirstCheck.current && data.count > prevUnreadCount.current) {
          const audio = new Audio('/sounds/pop.mp3');
          audio.volume = 0.5; 
          audio.play().catch(e => console.log("Браузер заблокировал автовоспроизведение звука", e));
        }

        isFirstCheck.current = false;
        prevUnreadCount.current = data.count;
        setUnreadCount(data.count);
      }
    } catch (e) {
      console.error("Ошибка проверки уведомлений:", e);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.oauthId) {
      checkUnreadNotifications(); 
      const interval = setInterval(checkUnreadNotifications, 30000); 
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, user?.oauthId]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        !modesRef.current?.contains(event.target as Node) &&
        !modesMenuRef.current?.contains(event.target as Node)
      ) {
        setIsModesOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileModesOpen(false);
    setIsModesOpen(false);
    setIsInboxOpen(false);
  }, [pathname]);

  const stateRefs = useRef({ isModesOpen, isMobileModesOpen, isMobileMenuOpen });
  useEffect(() => {
    stateRefs.current = { isModesOpen, isMobileModesOpen, isMobileMenuOpen };
  }, [isModesOpen, isMobileModesOpen, isMobileMenuOpen]);

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
    const updateViewport = (e: MediaQueryListEvent | MediaQueryList) => {
      const isDesktop = e.matches;
      setIsDesktopViewport(isDesktop);
      
      const { isModesOpen, isMobileModesOpen, isMobileMenuOpen } = stateRefs.current;
      
      if (isDesktop) {
        if (isMobileMenuOpen || isMobileModesOpen) {
          if (isMobileModesOpen) setIsModesOpen(true);
          setIsMobileMenuOpen(false);
          setIsMobileModesOpen(false);
        }
      } else {
        if (isModesOpen) {
          setIsMobileMenuOpen(true);
          setIsMobileModesOpen(true);
          setIsModesOpen(false);
        }
      }
    };
    updateViewport(desktopMediaQuery);
    
    const handler = (e: MediaQueryListEvent) => updateViewport(e);
    desktopMediaQuery.addEventListener('change', handler);
    return () => desktopMediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const isLocked = isMobileMenuOpen || isModesOpen;

    window.dispatchEvent(new CustomEvent('mobile-menu-toggle', { detail: isMobileMenuOpen }));

    if (isLocked) {
      document.body.style.overflow = 'hidden';
      window.dispatchEvent(new CustomEvent('lenis-lock', { detail: { isLocked: true } }));
    } else {
      document.body.style.overflow = '';
      window.dispatchEvent(new CustomEvent('lenis-lock', { detail: { isLocked: false } }));
    }

    return () => {
      document.body.style.overflow = '';
      window.dispatchEvent(new CustomEvent('lenis-lock', { detail: { isLocked: false } }));
      window.dispatchEvent(new CustomEvent('mobile-menu-toggle', { detail: false }));
    };
  }, [isMobileMenuOpen, isModesOpen]);

  useLayoutEffect(() => {
    const content = mobileModesContentRef.current;
    if (!content || !isMobileModesOpen) {
      setMobileModesHeight(0);
      return;
    }

    const updateHeight = () => setMobileModesHeight(content.scrollHeight);
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [isMobileModesOpen, activeTab]);

  useLayoutEffect(() => {
    const content = mobileInboxContentRef.current;
    const isMobileInboxOpen = isInboxOpen && !isDesktopViewport;
    if (!content || !isMobileInboxOpen) {
      setMobileInboxHeight(0);
      return;
    }

    const updateHeight = () => setMobileInboxHeight(content.scrollHeight);
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [isInboxOpen, isDesktopViewport]);

  const isActive = (path: string) => pathname === path;
  const isModesActive = isActive('/about/multiverse') || isActive('/creative');

  const handleMarkAsReadSuccess = () => {
    setUnreadCount(0);
    prevUnreadCount.current = 0;
  };

  const getNavBtnClass = (active: boolean) => `
    px-3 xl:px-4 py-2.5 xl:py-3 text-[14px] xl:text-[16px] font-medium leading-none whitespace-nowrap 
    transition-all duration-200 outline-none flex items-center justify-center cursor-pointer select-none
    ${active 
      ? 'bg-white/[0.28] text-white shadow-sm rounded-[12px]' 
      : 'bg-white/[0.12] text-white hover:bg-white/[0.20] rounded-[14px] hover:rounded-[12px]'
    }
  `;

  const tabData = {
    multiverse: {
      cards: [
        { title: "Основное выживание с экономикой", meta: "Версия 1.20+ • Стабильный ТПС", href: "/about/multiverse" },
        { title: "Динамическая онлайн-карта всех миров", meta: "Обновление в реальном времени", href: "/map" },
        { title: "Кланы, торговля и прокачка навыков", meta: "Гайд для начинающих", href: "/wiki" }
      ],
      bottom: { title: "Все о сервере Multiverse", sub: "Правила, особенности генерации и команды", href: "/about/multiverse" }
    },
    creative: {
      cards: [
        { title: "Персональные участки и приват", meta: "Большие плоты для строительства", href: "/about/creative" },
        { title: "WorldEdit и набор инструментов", meta: "Быстрое копирование и вставка схем", href: "/wiki" },
        { title: "Сезонные конкурсы архитекторов", meta: "Призы и сохранение в галерею", href: "/about/creative" }
      ],
      bottom: { title: "Все о режиме Creative", sub: "Справочник команд и механики стройки", href: "/about/creative" }
    },
    all: {
      cards: [
        { title: "Список всех доступных серверов", meta: "Текущий онлайн и статусы нод", href: "/stats" },
        { title: "База знаний и часто задаваемые вопросы", meta: "Ответы администрации", href: "/wiki" },
        { title: "Таблица лидеров и статистика", meta: "Топ игроков по часам и балансу", href: "/stats" }
      ],
      bottom: { title: "Правила и регламент проекта", sub: "Ознакомьтесь перед началом игры", href: "/rules" }
    }
  };

  const currentTab = tabData[activeTab];

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/60 z-155 transition-opacity duration-400 ease-out ${
          isModesOpen || isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => {
          setIsModesOpen(false);
          setIsMobileMenuOpen(false);
          setIsMobileModesOpen(false);
        }}
      />
      
      {isMobileMenuOpen && <div className="h-[76px] lg:hidden" />}

      <header className={`w-full transition-all duration-300 ${
        isMobileMenuOpen 
          ? 'fixed top-0 left-0 right-0 z-[180] bg-[#0e1017]' 
          : `sticky top-0 z-160 ${scrolled ? 'bg-[#111111]/85 backdrop-blur-md' : 'bg-transparent'}`
      }`}>
        <div className="w-full max-w-[120rem] mx-auto relative flex items-center justify-between gap-3 xl:gap-5 h-[76px] px-4 md:px-14 xl:px-[11.25rem]">
          
          <div className="flex items-center gap-4 xl:gap-8 min-w-0">
            <Link href="/" className="flex items-center shrink-0 translate-y-[1px]">
              <Image 
                src="/logo.png" 
                alt="Logo" 
                width={scrolled ? 100 : 135} 
                height={32} 
                className={`transition-all duration-300 w-auto ${scrolled ? 'h-8' : 'h-10'}`} 
                unoptimized 
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1.5 xl:gap-3 min-w-0">
              
              <div className="static" ref={modesRef}>
                <button 
                  onClick={() => setIsModesOpen(!isModesOpen)} 
                  className={`${getNavBtnClass(isModesOpen || isModesActive)} gap-2`}
                >
                  <span>{th.modes}</span> 
                  <ChevronDown size={16} className={`transition-transform duration-200 ${isModesOpen ? 'rotate-180 translate-y-[0.5px]' : 'translate-y-[1px]'}`}/>
                </button>

                <div 
                  ref={modesMenuRef}
                  className={`absolute top-full left-1/2 mt-3 w-[68vw] min-w-[840px] max-w-[1200px] -translate-x-1/2 z-50 transform-gpu transition-all duration-150 ease-out ${
                    isModesOpen 
                      ? 'opacity-100 translate-y-0 pointer-events-auto' 
                      : 'opacity-0 -translate-y-1 pointer-events-none'
                  }`}
                >
                  <div className="bg-[#0e1017] border border-[#232734] rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.7)] px-8 py-8 text-white">
                    <div className="flex gap-10">
                      
                      <div className="w-[190px] shrink-0 flex flex-col justify-between">
                        <div className="flex flex-col gap-1.5">
                          <button
                            onMouseEnter={() => setActiveTab('multiverse')}
                            onClick={() => setActiveTab('multiverse')}
                            className={`w-full text-left px-4 py-2.5 rounded-xl text-[16px] transition-all duration-300 ease-out transform active:scale-[0.98] cursor-pointer ${
                              activeTab === 'multiverse'
                                ? 'bg-[#1c202d] text-white font-semibold scale-[1.04]'
                                : 'text-[#8b919e] hover:text-white hover:bg-white/[0.03] font-medium hover:scale-[1.04]'
                            }`}
                          >
                            Multiverse
                          </button>

                          <button
                            onMouseEnter={() => setActiveTab('creative')}
                            onClick={() => setActiveTab('creative')}
                            className={`w-full text-left px-4 py-2.5 rounded-xl text-[16px] transition-all duration-300 ease-out transform active:scale-[0.98] cursor-pointer ${
                              activeTab === 'creative'
                                ? 'bg-[#1c202d] text-white font-semibold scale-[1.04]'
                                : 'text-[#8b919e] hover:text-white hover:bg-white/[0.03] font-medium hover:scale-[1.04]'
                            }`}
                          >
                            Creative
                          </button>

                          <button
                            onMouseEnter={() => setActiveTab('all')}
                            onClick={() => setActiveTab('all')}
                            className={`w-full text-left px-4 py-2.5 rounded-xl text-[16px] transition-all duration-300 ease-out transform active:scale-[0.98] cursor-pointer ${
                              activeTab === 'all'
                                ? 'bg-[#1c202d] text-white font-semibold scale-[1.04]'
                                : 'text-[#8b919e] hover:text-white hover:bg-white/[0.03] font-medium hover:scale-[1.04]'
                            }`}
                          >
                            Все режимы
                          </button>
                        </div>

                        <div className="flex flex-col gap-2 pt-6">
                          <Link
                            href="/wiki"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-4 py-1 text-[15px] font-medium text-[#8b919e] hover:text-white transition-colors"
                          >
                            <span>База знаний</span>
                            <ArrowUpRight size={16} className="opacity-70" />
                          </Link>
                          
                          <Link
                            href="/rules"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-4 py-1 text-[15px] font-medium text-[#8b919e] hover:text-white transition-colors"
                          >
                            <span>Правила</span>
                            <ArrowUpRight size={16} className="opacity-70" />
                          </Link>
                        </div>
                      </div>

                      <div className="flex-1 flex flex-col justify-between">
                        <div className="grid grid-cols-3 gap-3">
                          {currentTab.cards.map((card, idx) => (
                            <Link
                              key={idx}
                              href={card.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex min-h-[200px] flex-col justify-between rounded-[14px] p-6 transition-colors hover:bg-white/[0.06]"
                            >
                              <div className="text-[17px] font-semibold text-white group-hover:text-zinc-300 leading-snug">
                                {card.title}
                              </div>
                              <div className="flex items-center justify-between text-[14px] text-[#787f8f] mt-5">
                                <span className="truncate pr-1">{card.meta}</span>
                                <ArrowUpRight 
                                  size={16} 
                                  className="shrink-0 text-[#787f8f] group-hover:text-white transition-colors" 
                                />
                              </div>
                            </Link>
                          ))}
                        </div>

                        <div className="pt-6 mt-6 border-t border-[#1e222f]">
                          <Link
                            href={currentTab.bottom.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex max-w-full flex-col rounded-[14px] p-4 transition-colors hover:bg-white/[0.06]"
                          >
                            <div className="text-[16px] font-semibold text-white group-hover:text-zinc-300">
                              {currentTab.bottom.title}
                            </div>
                            <div className="mt-1 flex items-center gap-2 text-[14px] text-[#787f8f]">
                              <span>{currentTab.bottom.sub}</span>
                              <ArrowUpRight
                                size={17}
                                className="shrink-0 text-[#787f8f] group-hover:text-white transition-colors"
                              />
                            </div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Link href="/map" className={getNavBtnClass(isActive('/map'))}>{th.map}</Link>
              <Link href="/stats" className={getNavBtnClass(isActive('/stats'))}>{th.stats}</Link>
              <Link href="/shop" className={getNavBtnClass(isActive('/shop'))}>{th.shop}</Link>
              
              <Link href="/wiki" className={`${getNavBtnClass(isActive('/wiki'))} gap-2`}>
                <span>{th.wiki ?? 'Wiki'}</span>
                <ExternalLink size={16} className="opacity-70" />
              </Link>
            </nav>
          </div>

          <div className="hidden lg:flex items-center gap-3 xl:gap-6 shrink-0 h-full">
            
            {!isLoading && isAuthenticated && (
              <div className="relative flex items-center h-full">
                <button 
                  ref={desktopInboxTriggerRef}
                  onClick={() => setIsInboxOpen(!isInboxOpen)} 
                  className="relative flex items-center justify-center text-white transition-opacity duration-200 hover:opacity-65 outline-none cursor-pointer p-2"
                >
                  <MessageCircle size={24} strokeWidth={1.5} />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 flex items-center justify-center w-[18px] h-[18px] bg-red-500 rounded-full border-[2px] border-[#111111] text-[9px] font-bold text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                <InboxDropdown 
                  isOpen={isInboxOpen && isDesktopViewport} 
                  onClose={() => setIsInboxOpen(false)} 
                  user={user} 
                  onMarkAsReadSuccess={handleMarkAsReadSuccess}
                  triggerRef={desktopInboxTriggerRef}
                />
              </div>
            )}

            {!isLoading && (
              isAuthenticated && user?.nickname ? (
                <UserNav 
                  mode="account-menu"
                  user={user} 
                  logout={logout} 
                  showBalance={true} 
                  onOpenDeposit={() => setIsDepositOpen(true)} 
                  alignDropdownToHeader
                />
              ) : (
                <button 
                  onClick={openLoginModal} 
                  className={`${getNavBtnClass(false)} gap-2.5`}
                >
                  <User size={18} strokeWidth={1.8} />
                  <span>{th.login}</span>
                </button>
              )
            )}
          </div>

          <div
            className={`absolute left-0 right-0 top-full z-50 max-h-[calc(100dvh-4.5rem)] overflow-y-auto border-t border-white/10 bg-[#0e1017] px-5 pb-6 pt-4 text-white shadow-[0_20px_50px_rgba(0,0,0,0.7)] transition-all duration-200 lg:hidden ${
              isMobileMenuOpen
                ? 'visible translate-y-0 opacity-100'
                : 'invisible -translate-y-2 opacity-0 pointer-events-none'
            }`}
          >
            <nav className="flex flex-col gap-2">
              <button
                type="button"
                aria-expanded={isMobileModesOpen}
                onClick={() => setIsMobileModesOpen(!isMobileModesOpen)}
                className="flex w-full items-center justify-between rounded-xl bg-white/[0.06] px-4 py-3 text-left text-base font-medium cursor-pointer"
              >
                <span>{th.modes}</span>
                <ChevronDown size={18} className={`transition-transform ${isMobileModesOpen ? 'rotate-180' : ''}`} />
              </button>

              <div className="overflow-hidden transition-[height] duration-300 ease-in-out" style={{ height: `${mobileModesHeight}px` }} aria-hidden={!isMobileModesOpen}>
                <div ref={mobileModesContentRef} className="pt-2">
                  <div className="rounded-2xl border border-[#232734] bg-[#11141d] p-3">
                    <div className="mb-3 grid grid-cols-3 gap-2">
                      {([
                        ['multiverse', 'Multiverse'],
                        ['creative', 'Creative'],
                        ['all', 'Все режимы']
                      ] as const).map(([tab, label]) => (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setActiveTab(tab)}
                          className={`rounded-lg px-2 py-2 text-sm transition-colors cursor-pointer ${
                            activeTab === tab
                              ? 'bg-[#1c202d] font-semibold text-white'
                              : 'text-[#c5c9d2] hover:bg-white/[0.08] hover:text-white'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-col gap-1">
                      {currentTab.cards.map((card, idx) => (
                        <Link
                          key={idx}
                          href={card.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start justify-between gap-3 rounded-xl p-3 transition-colors hover:bg-white/[0.06] cursor-pointer"
                        >
                          <span>
                            <span className="block text-sm font-semibold text-white">{card.title}</span>
                            <span className="mt-1 block text-xs text-[#8b919e]">{card.meta}</span>
                          </span>
                          <ArrowUpRight size={17} className="mt-0.5 shrink-0 text-[#8b919e]" />
                        </Link>
                      ))}
                      <Link
                        href={currentTab.bottom.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start justify-between gap-3 rounded-xl p-3 transition-colors hover:bg-white/[0.06] cursor-pointer"
                      >
                        <span>
                          <span className="block text-sm font-semibold text-white">{currentTab.bottom.title}</span>
                          <span className="mt-1 block text-xs text-[#8b919e]">{currentTab.bottom.sub}</span>
                        </span>
                        <ArrowUpRight size={17} className="mt-0.5 shrink-0 text-[#8b919e]" />
                      </Link>
                    </div>
                    <div className="mt-2 flex gap-4 border-t border-[#1e222f] px-3 pt-3 text-sm text-[#8b919e]">
                      <Link href="/wiki" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">База знаний</Link>
                      <Link href="/rules" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">Правила</Link>
                    </div>
                  </div>
                </div>
              </div>

              {[
                [th.map, '/map'],
                [th.stats, '/stats'],
                [th.shop, '/shop'],
                [th.wiki ?? 'Wiki', '/wiki']
              ].map(([label, href]) => (
                <Link
                  key={href}
                  href={href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`rounded-xl px-4 py-3 text-base font-medium transition-colors cursor-pointer ${
                    isActive(href) ? 'bg-white/[0.16] text-white' : 'text-[#c5c9d2] hover:bg-white/[0.08] hover:text-white'
                  }`}
                >
                  {label}
                </Link>
              ))}

              {!isLoading && isAuthenticated && (
                <>
                  <button
                    type="button"
                    aria-expanded={isInboxOpen && !isDesktopViewport}
                    onClick={() => {
                      setIsInboxOpen(!isInboxOpen);
                      if (!isInboxOpen) setIsMobileModesOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded-xl bg-white/[0.06] px-4 py-3 text-left text-base font-medium cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span>{notificationLabel}</span>
                      {unreadCount > 0 && (
                        <span className="flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </div>
                    <ChevronDown size={18} className={`transition-transform ${isInboxOpen && !isDesktopViewport ? 'rotate-180' : ''}`} />
                  </button>
                  <div
                    className="overflow-hidden transition-[height] duration-300 ease-in-out"
                    style={{ height: `${mobileInboxHeight}px` }}
                    aria-hidden={!(isInboxOpen && !isDesktopViewport)}
                  >
                    <div ref={mobileInboxContentRef} className="pt-2">
                      <InboxDropdown
                        isOpen={isInboxOpen && !isDesktopViewport}
                        onClose={() => setIsInboxOpen(false)}
                        user={user}
                        onMarkAsReadSuccess={handleMarkAsReadSuccess}
                        inline
                      />
                    </div>
                  </div>
                </>
              )}
            </nav>
          </div>

          <div className="relative z-[60] ml-auto flex items-center gap-2 lg:hidden">
            <button
              type="button"
              aria-label={isMobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex h-11 w-11 items-center justify-center rounded-xl text-white transition-colors hover:bg-white/[0.12] cursor-pointer"
            >
              {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>

            {!isLoading && (
              isAuthenticated && user?.nickname ? (
                <UserNav
                  mode="account-menu"
                  user={user}
                  logout={logout}
                  showBalance={true}
                  onOpenDeposit={() => setIsDepositOpen(true)}
                />
              ) : (
                <button
                  type="button"
                  onClick={openLoginModal}
                  className="flex h-10 items-center justify-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-[#111111] transition-colors hover:bg-white/90 cursor-pointer"
                >
                  <User size={17} />
                  <span>{th.login}</span>
                </button>
              )
            )}
          </div>
        </div>
      </header>

      <DepositModal isOpen={isDepositOpen} onClose={() => setIsDepositOpen(false)} user={user} />
    </>
  );
}